/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import NewTable from "../../../components/NewTable/NewTable";
import { connect } from "react-redux";
import { alertActions, monitoringUnitsAction } from "../../../_actions";
import Loader from "../../../components/Atoms/Loader";
import Pagination from "../../../components/Pagination/Pagination";
import { capitalizeWords, formatDateByFormat, stripHtml } from "../../../_helpers";
import Nodata from "../../../components/Atoms/NoData/NoData";
import { FaEdit, FaRegCopy, FaRegEye } from "react-icons/fa";
import LoginDataDisplay from "./LoginDataDisplay";
import ConfirmModal from "../../../components/Atoms/popup/ConfirmModal";
import { Link } from "react-router-dom";
import SwapDetailsModal from "./SwapDetailsModal";
import OrderDetailsModal from "./OrderDetailsModal";
import RedeemDetailsModal from "./RedeemDetailsModal";
import ModelPreviewDefault from "../../../components/Atoms/ModelDefault/ModelPreviewDefault";
import NoData from "../../../components/Atoms/NoData";
import { FaArrowTrendDown, FaArrowTrendUp } from "react-icons/fa6";

const tabOptions = [
  {
    label: "Transaction Monitoring (User)",
    value: "transaction-monitoring-user",
    keys: ["send", "receive", "swap", "express", "buy", "sell", "redeem"],
    tableHeader: ["User", "From", "To", "Hash", "Amount", "Status", "Date"],
  },
  {
    label: "IP Address Analysis",
    value: "ip-address-analysis",
    keys: ["flags", "signup", "new", "frequent", "usage", "login"],
    tableHeader: [
      "User",
      "IP Address",
      "User Agent",
      "Location",
      "Status",
      "Date & Time",
    ],
  },
  {
    label: "Whitelisting / Blacklisting",
    value: "whitelisting-blacklisting",
    keys: ["whitelist-blacklist-flags"],
    tableHeader: [
      "IP Address",
      "User Agent",
      "Location",
      "Usage Count",
      "Status",
      "Last Usage At",
      "Action",
    ],
  },
  {
    label: "Credit Score Analysis",
    value: "credit-score-analysis",
    keys: ["credit-score-user"],
    tableHeader: ["User", "Credit Score", "Last Modified At", "Action"],
  },
  {
    label: "Backups",
    value: "backups",
    keys: ["backup-pattern"],
  },
  {
    label: "Transaction Monitoring (Merchant)",
    value: "transaction-monitoring-merchant",
    keys: ["merchant-express", "redeem-merchant"],
    tableHeader: [
      "User",
      "Store",
      "From",
      "To",
      "Hash",
      "Amount",
      "Status",
      "Date",
    ],
  },
  {
    label: "Fees Analysis",
    value: "fees-analysis",
    keys: [
      "swap-fees",
      "user-redeem-fees",
      "merchant-redeem-fees",
      "user-express-fees",
      "merchant-express-fees",
    ],
    tableHeader: [
      "Date / Month",
      "Type",
      "Network Fees",
      "Knowledge Fees",
      "Innovation Fees",
      "TotalFees",
    ],
  },
  {
    label: "Merchant Analysis",
    value: "merchant-analysis",
    keys: ["merchant-apps", "sub-merchant-apps"],
    tableHeader: [
      "User",
      "Store",
      "Address",
      "Balance",
      "Frozen Balance",
      "Account Status",
      "Last Transaction Date",
    ],
  },
];

const size = 10;

const defaultScoreLevels = {
  "8-10": {
    label: "Trusted",
    riskLevel: "Low",
    accessLevel: "Full features unlocked",
    remarks:
      "User maintains excellent credibility — all platform features available without restriction.",
  },
  "5-7": {
    label: "Medium Risk",
    riskLevel: "Moderate",
    accessLevel: "Some features limited (reduced P2P and withdrawal limits)",
    remarks:
      "User shows average reliability — partial access granted with trading and withdrawal limits.",
  },
  "3-4": {
    label: "High Risk",
    riskLevel: "High",
    accessLevel: "View balance only — no trading or withdrawals allowed",
    remarks:
      "User account flagged for high-risk behavior — restricted to viewing balance until review.",
  },
  "1-2": {
    label: "Suspended",
    riskLevel: "Critical",
    accessLevel: "Account frozen — requires admin review to unlock",
    remarks:
      "User score indicates critical risk — automatic suspension applied pending manual verification.",
  },
};

function getScoreLevelInfo(creditScore, creditScoreLevels) {
  const levels = creditScoreLevels || defaultScoreLevels;
  const numScore =
    typeof creditScore === "number" ? creditScore : parseFloat(creditScore);

  if (isNaN(numScore)) {
    return {
      label: "N/A",
      riskLevel: "N/A",
      accessLevel: "N/A",
      remarks: "N/A",
    };
  }

  let key = "1-2";
  if (numScore >= 8) {
    key = "8-10";
  } else if (numScore >= 5) {
    key = "5-7";
  } else if (numScore >= 3) {
    key = "3-4";
  } else {
    key = "1-2";
  }

  return levels?.[key] || defaultScoreLevels[key];
}

const getScoreColor = (score) => {
  const numScore = typeof score === "number" ? score : parseFloat(score);
  if (isNaN(numScore)) return "text-gray-600";
  if (numScore >= 8) return "text-green-600"; // Trusted
  if (numScore >= 5) return "text-yellow-500"; // Medium Risk
  if (numScore >= 3) return "text-orange-500"; // High Risk
  return "text-red-600"; // Suspended
};

const DetailsPage = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const queryTab = queryParams.get("tab");
  const queryMonitoring = queryParams.get("monitoring");
  const activeTabData = tabOptions.find((t) => t.value === queryTab);
  const isMonitoringValid = activeTabData?.keys.includes(queryMonitoring);
  const {
    ipAddressMonitoringList,
    ipAddressData,
    loading,
    getCountryCodeList,
    countryList,
    whiteBlackListData,
    ipAddressWhiteAndBlackListing,
    showErrorMessage,
    showSuccessMessage,
    transactionMonitoring,
    transactionListData,
    feesMonitoringList,
    feesAnalysisListData,
    webStoresListData,
    webStoresMonitoring,
    userCreditScoreMonitoringData,
    userCreditScoreMonitoring,
  } = props;
  const [pageNo, setPageNo] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFlag, setSelectedFlag] = useState({
    label: "All",
    value: "ALL",
  });

  const [selectedStatus, setSelectedStatus] = useState("All");
  const [openHistoryModal, setOpenHistoryModal] = useState({
    isOpen: false,
    data: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  const [openConfirmationModal, setOpenConfirmationModal] = useState({
    isOpen: false,
    data: null,
    heading: "",
    subheading: "",
    confirmLabel: "",
  });

  const [isRefresh, setIsRefresh] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [openDetailModal, setOpenDetailModal] = useState({
    isOpen: false,
    data: null,
    type: "",
  });

  const [selectedGroup, setSelectedGroup] = useState("DATE");

  const [openScoreHistoryModal, setOpenScoreHistoryModal] = useState({
    isOpen: false,
    data: null,
  });

  const statusStyles = {
    pending: "bg-[#FFA600]",
    completed: "bg-[#00811D]",
    failed: "bg-[#FF4C4C]",
    canceled: "bg-gray-500",
  };

  const onPageChange = (page) => {
    setPageNo(page);
  };

  const handleViewIpAddressHistory = (ipAddress) => {
    setIsLoading(true);
    monitoringUnitsAction
      .viewIpAddressHistoryByPromise({ ipAddress })
      .then((response) => {
        setOpenHistoryModal({
          isOpen: true,
          data: response.data,
        });
      })
      .catch((error) => {
        showErrorMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleUpdateIpAddressStatus = (ipAddress, isWhiteListed) => {
    setOpenConfirmationModal({
      isOpen: true,
      data: { ipAddress, isWhiteListed },
      heading: isWhiteListed
        ? "Confirm IP Blacklisting"
        : "Confirm IP Whitelisting",
      subheading: isWhiteListed
        ? "This action will blacklist the IP address. Are you sure you want to continue?"
        : "This action will whitelist the IP address. Are you sure you want to continue?",
      confirmLabel: isWhiteListed ? "Blacklist IP" : "Whitelist IP",
    });
  };

  const handleSearchAbleSelect = (data) => {
    setSelectedFlag(data);
  };

  const handleConfirmationOnIpAddressStatus = () => {
    setIsLoading(true);
    monitoringUnitsAction
      .convertIpAddressInWhiteOrBlackListByPromise({
        ipAddress: openConfirmationModal.data.ipAddress,
        status: openConfirmationModal.data.isWhiteListed
          ? "Blacklisted"
          : "Whitelisted",
      })
      .then((response) => {
        setOpenConfirmationModal({
          isOpen: false,
          data: null,
          heading: "",
          subheading: "",
          confirmLabel: "",
        });
        setIsRefresh((prev) => !prev);
        showSuccessMessage(response.message || "Success.");
      })
      .catch((error) => {
        showErrorMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    showSuccessMessage("Address copied to clipboard.");
  };

  const handleDateRangeChange = () => {
    setPageNo(1);
  };

  const handleViewCreditScoreHistory = (id) => {
    if (!id) {
      showErrorMessage("The credit score history ID is invalid.");
      return;
    }
    setIsLoading(true);
    monitoringUnitsAction
      .viewUserCreditScoreHistoryByPromise({ id })
      .then((response) => {
        if (response?.data) {
          setOpenScoreHistoryModal({
            isOpen: true,
            data: response.data,
          });
        } else {
          showErrorMessage(response?.message || "No history records found.");
        }
      })
      .catch((error) => {
        showErrorMessage(error?.message || error || "Unable to load credit score history. Please try again.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const ipAddressTableRows = useMemo(
    () =>
      ipAddressData?.list?.map((ele, index) => {
        const location = ele?.location;

        const locationDetails = location
          ? `${location.city || "-"}, ${location.region || "-"}, ${location.country || "-"
          }\nIP Range: ${location.range
            ? `${location.range[0]} - ${location.range[1]}`
            : "-"
          }`
          : "N/A";

        return [
          ele?.user || "-",
          ele.ipAddress,
          ele.userAgent,
          locationDetails,
          <div
            key={`status-${index}`}
            className={`inline-block px-2 py-1 rounded-full ${ele.status === "SUCCESS" ? "bg-[#29A74447]" : "bg-[#FF161633]"
              }`}
          >
            <span
              className={
                ele.status === "SUCCESS" ? "text-[#15A14A]" : "text-[#FF0000]"
              }
            >
              {ele.status}
            </span>
          </div>,
          formatDateByFormat(ele.loginTime),
        ];
      }),
    [ipAddressData]
  );

  const whiteBlackTableRows = useMemo(
    () =>
      whiteBlackListData?.list?.map((ele, index) => {
        const location = ele?.location;

        const locationDetails = location
          ? `${location.city || "-"}, ${location.region || "-"}, ${location.country || "-"
          }\nIP Range: ${location.range
            ? `${location.range[0]} - ${location.range[1]}`
            : "-"
          }`
          : "N/A";

        return [
          ele.ipAddress,
          ele.userAgent,
          locationDetails,
          ele.count,
          <div
            key={`status-${index}`}
            className={`inline-block px-2 py-1 rounded-full ${ele.isWhiteListed ? "bg-[#29A74447]" : "bg-[#FF161633]"
              }`}
          >
            <span
              className={
                ele.isWhiteListed ? "text-[#15A14A]" : "text-[#FF0000]"
              }
            >
              {ele.isWhiteListed ? "WhiteListed" : "BlackListed"}{" "}
            </span>
          </div>,
          formatDateByFormat(ele.loginTime),
          <div className="flex justify-start items-center flex-wrap gap-2">
            <span onClick={() => handleViewIpAddressHistory(ele.ipAddress)}>
              <FaRegEye
                className="text-[22px] text-blue-500"
                title="View History"
              />
            </span>
            <span
              onClick={() =>
                handleUpdateIpAddressStatus(ele.ipAddress, ele.isWhiteListed)
              }
            >
              <FaEdit
                className="text-[20px] text-blue-500"
                title="Change Status"
              />
            </span>
          </div>,
        ];
      }),
    [whiteBlackListData]
  );

  const transactionMonitoringRows = useMemo(
    () =>
      transactionListData?.list?.map((ele, index) => {
        const truncatedFrom =
          ele?.from?.slice(0, 15) + (ele?.from?.length > 15 ? "..." : "");
        const truncatedTo =
          ele?.to?.slice(0, 15) + (ele?.to?.length > 15 ? "..." : "");
        const truncatedHash =
          ele?.hash?.slice(0, 15) + (ele?.hash?.length > 15 ? "..." : "");
        const fullHashLink = `${transactionListData?.link}${ele?.hash}`;

        return [
          ...(queryMonitoring !== "merchant-express"
            ? [
              <div key={`user-${index}`}>
                <p className="font-medium">{ele?.userId?.name || "N/A"}</p>
                <p className="text-sm text-gray-600">
                  {ele?.userId?.email || "N/A"}
                </p>
              </div>,
            ]
            : [
              <div key={`user-${index}`}>
                <p className="font-medium">{ele?.userId?.name || "N/A"}</p>
                <p className="text-sm text-gray-600">
                  {ele?.userId?.email || "N/A"}
                </p>
              </div>,
              <div key={`store-${index}`}>
                <p className="font-medium capitalize">
                  {ele?.storeId?.storeName || "N/A"}
                </p>
                <p className="text-sm text-gray-600">
                  {ele?.storeId?.storeURL || "N/A"}
                </p>
              </div>,
            ]),
          <div
            className="relative flex items-center group"
            key={`from-${index}`}
          >
            <span className="truncate" title={ele?.from}>
              {truncatedFrom || "N/A"}
            </span>
            <div className="absolute left-0 px-2 py-1 mb-2 text-xs text-white transition-opacity duration-200 bg-black rounded opacity-0 bottom-full group-hover:opacity-100">
              {ele?.from}
            </div>
            {ele?.from && (
              <span
                className="ml-2 cursor-pointer hover:text-blue-500"
                onClick={() => handleCopy(ele.from)}
                title="Copy sender address"
              >
                <FaRegCopy />
              </span>
            )}
          </div>,

          <div className="relative flex items-center group" key={`to-${index}`}>
            <span className="truncate" title={ele?.to}>
              {truncatedTo || "N/A"}
            </span>
            <div className="absolute left-0 px-2 py-1 mb-2 text-xs text-white transition-opacity duration-200 bg-black rounded opacity-0 bottom-full group-hover:opacity-100">
              {ele?.to}
            </div>
            {ele?.to && (
              <span
                className="ml-2 cursor-pointer hover:text-blue-500"
                onClick={() => handleCopy(ele.to)}
                title="Copy recipient address"
              >
                <FaRegCopy />
              </span>
            )}
          </div>,

          <div key={`hash-${index}`}>
            {ele?.hash ? (
              <Link
                to={fullHashLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline flex items-center"
              >
                {truncatedHash}
              </Link>
            ) : (
              <span>N/A</span>
            )}
          </div>,

          ["BUY", "SELL", "SWAP", "REDEEM", "REDEEM_MERCHANT"].includes(
            ele?.transactionType
          ) ? (
            <div className="flex flex-col max-w-[90px] ">
              <p className="font-medium mb-1">
                {Number(ele?.amount || 0).toFixed(4)} {ele?.coinType}
              </p>
              {ele?.referenceId && (
                <p
                  className="text-blue-600 underline cursor-pointer hover:text-blue-800 transition-colors text-sm text-center "
                  onClick={() =>
                    setOpenDetailModal({
                      isOpen: true,
                      type: ele?.transactionType,
                      data: ele,
                    })
                  }
                >
                  View
                </p>
              )}
            </div>
          ) : (
            <span className="font-medium">
              {Number(ele?.amount || 0).toFixed(4)}
            </span>
          ),

          <div className="flex capitalize items-center" key={`status-${index}`}>
            <div
              className={`w-1 h-4 mr-2 rounded-md ${statusStyles[ele?.status] || "bg-gray-500"
                }`}
            ></div>
            {ele?.status || "N/A"}
          </div>,

          <span key={`date-${index}`}>
            {ele?.createdAt ? formatDateByFormat(ele.createdAt) : "N/A"}
          </span>,
        ];
      }) || [],
    [transactionListData]
  );

  const feesAnalysisRows = useMemo(
    () =>
      feesAnalysisListData?.list?.map((ele, index) => {
        const totalCalculatedFees =
          (ele?.fees.networkFee || 0) +
          (ele?.fees.knowledgeFee || 0) +
          (ele?.fees.innovationFee || 0);

        return [
          ...(queryMonitoring === "swap-fees"
            ? [
              <span key={`date-${index}`}>
                {ele?.date
                  ? formatDateByFormat(
                    ele.date,
                    selectedGroup === "MONTH" ? "MMMM YYYY" : "MMM D, YYYY"
                  )
                  : "N/A"}
              </span>,
              <span key={`type-${index}`}>
                {ele?.type
                  ? capitalizeWords(ele.type.split("_").join(" "))
                  : "N/A"}
              </span>,
            ]
            : [
              <span key={`date-${index}`}>
                {ele?.date ? formatDateByFormat(ele.date) : "N/A"}
              </span>,
            ]),

          <span key={`networkFee-${index}`}>
            {Number(ele?.fees.networkFee || 0).toFixed(4)}{" "}
            {queryMonitoring === "swap-fees"
              ? ele.type.split("_TO_")[1]
              : "PKRD"}
          </span>,
          <span key={`knowledgeFee-${index}`}>
            {Number(ele?.fees.knowledgeFee || 0).toFixed(4)}{" "}
            {queryMonitoring === "swap-fees"
              ? ele.type.split("_TO_")[1]
              : "PKRD"}
          </span>,
          <span key={`innovationFee-${index}`}>
            {Number(ele?.fees.innovationFee || 0).toFixed(4)}{" "}
            {queryMonitoring === "swap-fees"
              ? ele.type.split("_TO_")[1]
              : "PKRD"}
          </span>,
          <span key={`totalFees-${index}`}>
            {Number(totalCalculatedFees).toFixed(4)}{" "}
            {queryMonitoring === "swap-fees"
              ? ele.type.split("_TO_")[1]
              : "PKRD"}
          </span>,
        ];
      }) || [],
    [feesAnalysisListData]
  );

  const webStoresMonitoringRows = useMemo(
    () =>
      webStoresListData?.list?.map((ele, index) => {
        const truncatedAddress =
          ele?.address?.slice(0, 15) + (ele?.address?.length > 15 ? "..." : "");

        return [
          <div key={`user-${index}`}>
            <p className="font-medium">{ele?.userId?.name || "N/A"}</p>
            <p className="text-sm text-gray-600">
              {ele?.userId?.email || "N/A"}
            </p>
          </div>,
          <div key={`store-${index}`}>
            <p className="font-medium capitalize">{ele?.storeName || "N/A"}</p>
            {ele?.storeURL ? (
              <Link
                to={ele?.storeURL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline flex items-center"
              >
                {ele?.storeURL}
              </Link>
            ) : (
              <p className="text-sm text-gray-600">N/A</p>
            )}
          </div>,
          <div
            className="relative flex items-center group"
            key={`address-${index}`}
          >
            <span className="truncate" title={ele?.address}>
              {truncatedAddress || "N/A"}
            </span>
            <div className="absolute left-0 px-2 py-1 mb-2 text-xs text-white transition-opacity duration-200 bg-black rounded opacity-0 bottom-full group-hover:opacity-100">
              {ele?.address}
            </div>
            {ele?.address && (
              <span
                className="ml-2 cursor-pointer hover:text-blue-500"
                onClick={() => handleCopy(ele.address)}
                title="Copy address"
              >
                <FaRegCopy />
              </span>
            )}
          </div>,

          <span className="font-medium">
            {Number(ele?.balance || 0).toFixed(4)} PKRD
          </span>,
          <span className="font-medium">
            {Number(ele?.freezedBalance || 0).toFixed(4)} PKRD
          </span>,

          <div className="flex capitalize items-center" key={`status-${index}`}>
            <div
              className={`w-1 h-4 mr-2 rounded-md ${ele?.isDisable ? "bg-[#FF4C4C]" : "bg-[#00811D]"
                }`}
            ></div>
            {ele?.status ? "Disabled" : "Enabled"}
          </div>,

          <span key={`date-${index}`}>
            {ele?.lastTransactionAt
              ? formatDateByFormat(ele.lastTransactionAt)
              : "N/A"}
          </span>,
        ];
      }) || [],
    [transactionListData]
  );

  const userCreditScoreMonitoringRows = useMemo(
    () =>
      userCreditScoreMonitoringData?.list?.map((ele, index) => {
        return [
          <div key={`user-${index}`}>
            <p className="font-medium">{ele?.userId?.name || "N/A"}</p>
            <p className="text-sm text-gray-600">
              {ele?.userId?.email || "N/A"}
            </p>
          </div>,

          <span className="font-medium">{ele?.creditScore ?? "N/A"}</span>,

          <span key={`date-${index}`}>
            {ele?.updatedAt ? formatDateByFormat(ele.updatedAt) : "N/A"}
          </span>,
          <div className="flex justify-start items-center flex-wrap gap-2">
            <span
              className="cursor-pointer"
              onClick={() => handleViewCreditScoreHistory(ele?._id || ele?.id)}
            >
              <FaRegEye
                className="text-[22px] text-gray-500 hover:text-blue-600 cursor-pointer"
                title="View Credit Score History"
              />
            </span>
          </div>,
        ];
      }) || [],
    [userCreditScoreMonitoringData]
  );

  useEffect(() => {
    if (!queryTab || !queryMonitoring || !isMonitoringValid) {
      navigate("/app/monitoring-unit/");
    }

    if (
      queryTab &&
      queryMonitoring &&
      isMonitoringValid &&
      queryTab === "ip-address-analysis"
    ) {
      const reqData = {
        keyWord: searchTerm,
        pageNo: pageNo,
        size: size,
        fromDate: "",
        toDate: "",
        sortBy: "loginTime",
        sortOrder: "desc",
        monitoring: queryMonitoring.toUpperCase(),
        country: selectedFlag ? selectedFlag.value : "",
      };
      ipAddressMonitoringList(reqData);
    }

    if (
      queryTab &&
      queryMonitoring &&
      isMonitoringValid &&
      queryTab === "whitelisting-blacklisting"
    ) {
      const reqData = {
        keyWord: searchTerm,
        pageNo: pageNo,
        size: size,
        fromDate: "",
        toDate: "",
        country: selectedFlag ? selectedFlag.value : "",
        status: selectedStatus,
      };
      ipAddressWhiteAndBlackListing(reqData);
    }

    if (
      queryTab &&
      queryMonitoring &&
      isMonitoringValid &&
      [
        "transaction-monitoring-user",
        "transaction-monitoring-merchant",
      ].includes(queryTab) &&
      [
        "send",
        "receive",
        "swap",
        "express",
        "buy",
        "sell",
        "merchant-express",
        "redeem",
        "redeem-merchant",
      ].includes(queryMonitoring)
    ) {
      const reqData = {
        keyWord: searchTerm,
        pageNo: pageNo,
        size: size,
        sortBy: "createdAt",
        sortOrder: "desc",
        fromDate: fromDate,
        toDate: toDate,
        status: selectedStatus === "All" ? "ALL" : selectedStatus,
        type: queryMonitoring.split("-").join("_").toUpperCase(),
      };
      transactionMonitoring(reqData);
    }

    if (
      queryTab &&
      queryMonitoring &&
      isMonitoringValid &&
      ["fees-analysis"].includes(queryTab) &&
      [
        "swap-fees",
        "user-redeem-fees",
        "merchant-redeem-fees",
        "user-express-fees",
        "merchant-express-fees",
      ].includes(queryMonitoring)
    ) {
      const type =
        queryMonitoring === "swap-fees"
          ? "SWAP"
          : ["user-redeem-fees", "merchant-redeem-fees"].includes(
            queryMonitoring
          )
            ? "REDEEM"
            : "EXPRESS";

      const subType = (() => {
        switch (queryMonitoring) {
          case "user-redeem-fees":
            return "INDIVIDUAL";
          case "merchant-redeem-fees":
            return "MERCHANT";
          case "user-express-fees":
            return "EXPRESS";
          case "merchant-express-fees":
            return "EXPRESS_MERCHANT";
          default:
            return selectedStatus === "All" ? "ALL" : selectedStatus;
        }
      })();
      const reqData = {
        pageNo: pageNo,
        size: size,
        fromDate: fromDate,
        toDate: toDate,
        type: type,
        subType: subType,
        groupBy: selectedGroup,
      };
      feesMonitoringList(reqData);
    }

    if (
      queryTab &&
      queryMonitoring &&
      isMonitoringValid &&
      ["merchant-analysis"].includes(queryTab) &&
      ["merchant-apps", "sub-merchant-apps"].includes(queryMonitoring)
    ) {
      const reqData = {
        keyWord: searchTerm,
        pageNo: pageNo,
        size: size,
        type: queryMonitoring.split("-").join("_").toUpperCase(),
      };
      webStoresMonitoring(reqData);
    }

    if (
      queryTab &&
      queryMonitoring &&
      isMonitoringValid &&
      ["merchant-analysis"].includes(queryTab) &&
      ["merchant-apps", "sub-merchant-apps"].includes(queryMonitoring)
    ) {
      const reqData = {
        keyWord: searchTerm,
        pageNo: pageNo,
        size: size,
        type: queryMonitoring.split("-").join("_").toUpperCase(),
      };
      webStoresMonitoring(reqData);
    }
    if (
      queryTab &&
      queryMonitoring &&
      isMonitoringValid &&
      ["credit-score-analysis"].includes(queryTab) &&
      ["credit-score-user"].includes(queryMonitoring)
    ) {
      const reqData = {
        keyWord: searchTerm,
        pageNo: pageNo,
        size: size,
      };
      userCreditScoreMonitoring(reqData);
    }
  }, [
    queryTab,
    queryMonitoring,
    isMonitoringValid,
    navigate,
    searchTerm,
    pageNo,
    ipAddressMonitoringList,
    selectedFlag,
    ipAddressWhiteAndBlackListing,
    selectedStatus,
    isRefresh,
    fromDate,
    toDate,
    selectedGroup,
  ]);

  useEffect(() => {
    getCountryCodeList();
  }, [getCountryCodeList]);

  return (
    <>
      <Loader loading={loading || isLoading} />
      {activeTabData && isMonitoringValid && (
        <>
          <div className="p-6 overflow-hidden overflow-y-auto">
            {queryTab === "ip-address-analysis" ? (
              <>
                <NewTable
                  Heading={capitalizeWords(
                    `${activeTabData.label} (${queryMonitoring})`
                  )}
                  tableHeadings={activeTabData.tableHeader}
                  data={ipAddressTableRows}
                  placeholder="Search by user, email, or IP address"
                  showSearch={true}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  showSorting={queryMonitoring === "flags"}
                  showDropDown1={false}
                  sortStatusValue={selectedFlag}
                  onSortByStatus={handleSearchAbleSelect}
                  sortingOptions={[
                    { label: "All", value: "ALL" },
                    ...countryList.map((con) => ({
                      label: `${con.emoji} ${con.name}`,
                      value: con.code,
                    })),
                  ]}
                  isSearchAbleSelect={true}
                />
                {Math.ceil(ipAddressData?.total / size) > 1 && (
                  <Pagination
                    totalPages={Math.ceil(ipAddressData?.total / size)}
                    currentPage={pageNo}
                    onPageChange={onPageChange}
                  />
                )}
              </>
            ) : queryTab === "whitelisting-blacklisting" ? (
              <>
                <NewTable
                  Heading={`${activeTabData.label}`}
                  tableHeadings={activeTabData.tableHeader}
                  data={whiteBlackTableRows}
                  placeholder="Search by IP address"
                  showSearch={true}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  showSorting={true}
                  showDropDown1={false}
                  sortStatusValue={selectedFlag}
                  onSortByStatus={handleSearchAbleSelect}
                  sortingOptions={[
                    { label: "All", value: "ALL" },
                    ...countryList.map((con) => ({
                      label: `${con.emoji} ${con.name}`,
                      value: con.code,
                    })),
                  ]}
                  isSearchAbleSelect={true}
                  showDropDown2={true}
                  sortStatusValue2={selectedStatus}
                  onSortByStatus2={(e) => {
                    setSelectedStatus(e.target.value);
                  }}
                  sortingOptions2={[
                    {
                      label: "All",
                      value: "All",
                    },
                    {
                      label: "Whitelisted",
                      value: "Whitelisted",
                    },
                    {
                      label: "Blacklisted",
                      value: "Blacklisted",
                    },
                  ]}
                  selectClassName2={"py-2 rounded-[4px]"}
                />
                {Math.ceil(whiteBlackListData?.total / size) > 1 && (
                  <Pagination
                    totalPages={Math.ceil(whiteBlackListData?.total / size)}
                    currentPage={pageNo}
                    onPageChange={onPageChange}
                  />
                )}
              </>
            ) : [
              "transaction-monitoring-user",
              "transaction-monitoring-merchant",
            ].includes(queryTab) &&
              [
                "send",
                "receive",
                "swap",
                "express",
                "buy",
                "sell",
                "merchant-express",
                "redeem",
                "redeem-merchant",
              ].includes(queryMonitoring) ? (
              <>
                <NewTable
                  Heading={`Transaction Monitoring (${capitalizeWords(
                    queryMonitoring.split("-").join(" ")
                  )})`}
                  tableHeadings={activeTabData.tableHeader}
                  data={transactionMonitoringRows}
                  placeholder="Search by address..."
                  showSearch={true}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  showSorting={true}
                  showDropDown2={true}
                  sortStatusValue2={selectedStatus}
                  onSortByStatus2={(e) => {
                    setSelectedStatus(e.target.value);
                  }}
                  sortingOptions2={[
                    { value: "All", label: "All statuses" },
                    { value: "pending", label: "Pending" },
                    { value: "completed", label: "Completed" },
                    { value: "failed", label: "Failed" },
                    { value: "canceled", label: "Canceled" },
                  ]}
                  fromDate={fromDate}
                  setFromDate={setFromDate}
                  setToDate={setToDate}
                  toDate={toDate}
                  showDateFilter={true}
                  handleDateRangeChange={handleDateRangeChange}
                />
                {Math.ceil(transactionListData?.total / size) > 1 && (
                  <Pagination
                    totalPages={Math.ceil(transactionListData?.total / size)}
                    currentPage={pageNo}
                    onPageChange={onPageChange}
                  />
                )}
              </>
            ) : queryTab === "fees-analysis" ? (
              <>
                <NewTable
                  Heading={`Fees Analysis (${capitalizeWords(
                    queryMonitoring.split("-").join(" ")
                  )})`}
                  tableHeadings={
                    queryMonitoring === "swap-fees"
                      ? activeTabData.tableHeader
                      : activeTabData.tableHeader.filter(
                        (head) => head !== "Type"
                      )
                  }
                  data={feesAnalysisRows}
                  showSearch={false}
                  showSorting={true}
                  fromDate={fromDate}
                  setFromDate={setFromDate}
                  setToDate={setToDate}
                  toDate={toDate}
                  showDateFilter={false}
                  handleDateRangeChange={handleDateRangeChange}
                  showDropDown1={true}
                  sortStatusValue={selectedGroup}
                  onSortByStatus={(e) => {
                    setSelectedGroup(e.target.value);
                    setFromDate("");
                    setToDate("");
                  }}
                  sortingOptions={[
                    {
                      label: "Group By Date",
                      value: "DATE",
                    },
                    {
                      label: "Group By Month",
                      value: "MONTH",
                    },
                  ]}
                  showDualDateFilter={true}
                  dateFormat={selectedGroup.toLowerCase()}
                />
                {Math.ceil(feesAnalysisListData?.total / size) > 1 && (
                  <Pagination
                    totalPages={Math.ceil(feesAnalysisListData?.total / size)}
                    currentPage={pageNo}
                    onPageChange={onPageChange}
                  />
                )}
              </>
            ) : queryTab === "merchant-analysis" ? (
              <>
                <NewTable
                  Heading={capitalizeWords(
                    `${activeTabData.label} (${queryMonitoring
                      .split("-")
                      .join(" ")})`
                  )}
                  tableHeadings={activeTabData.tableHeader}
                  data={webStoresMonitoringRows}
                  placeholder="Search by address..."
                  showSearch={true}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                />
                {Math.ceil(webStoresListData?.total / size) > 1 && (
                  <Pagination
                    totalPages={Math.ceil(webStoresListData?.total / size)}
                    currentPage={pageNo}
                    onPageChange={onPageChange}
                  />
                )}
              </>
            ) : queryTab === "credit-score-analysis" ? (
              <>
                <NewTable
                  Heading={capitalizeWords(
                    `${activeTabData.label} (${queryMonitoring
                      .split("-")
                      .join(" ")})`
                  )}
                  tableHeadings={activeTabData.tableHeader}
                  data={userCreditScoreMonitoringRows}
                  placeholder="Search by user..."
                  showSearch={true}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                />
                {Math.ceil(userCreditScoreMonitoringData?.total / size) > 1 && (
                  <Pagination
                    totalPages={Math.ceil(
                      userCreditScoreMonitoringData?.total / size
                    )}
                    currentPage={pageNo}
                    onPageChange={onPageChange}
                  />
                )}
              </>
            ) : (
              <div className="border border-[#9d9d9e69] rounded-md mt-2">
                <Nodata nodataLabel={"No monitoring records found."} />
              </div>
            )}
          </div>
          <LoginDataDisplay
            data={openHistoryModal.data}
            isOpen={openHistoryModal.isOpen}
            closeModel={() =>
              setOpenHistoryModal({
                isOpen: false,
                data: [],
              })
            }
            previewHeading={"IP Address Usage History"}
            childrenClassName={"p-3"}
          />
          <ConfirmModal
            isOpen={openConfirmationModal.isOpen}
            onClose={() =>
              setOpenConfirmationModal({
                isOpen: false,
                data: null,
                heading: "",
                subheading: "",
                confirmLabel: "",
              })
            }
            onConfirm={handleConfirmationOnIpAddressStatus}
            heading={openConfirmationModal.heading}
            subheading={openConfirmationModal.subheading}
            cancelLabel={"Go Back"}
            confirmLabel={openConfirmationModal.confirmLabel || "Confirm"}
          />
          <SwapDetailsModal
            isOpen={openDetailModal.isOpen && openDetailModal.type === "SWAP"}
            closeModel={() => {
              setOpenDetailModal({ isOpen: false, data: null, type: "" });
            }}
            ele={openDetailModal.data}
          />

          <OrderDetailsModal
            isOpen={
              openDetailModal.isOpen &&
              ["BUY", "SELL"].includes(openDetailModal.type)
            }
            closeModel={() => {
              setOpenDetailModal({ isOpen: false, data: null, type: "" });
            }}
            ele={openDetailModal.data}
          />

          <RedeemDetailsModal
            isOpen={
              openDetailModal.isOpen &&
              ["REDEEM", "REDEEM_MERCHANT"].includes(openDetailModal.type)
            }
            closeModel={() => {
              setOpenDetailModal({ isOpen: false, data: null, type: "" });
            }}
            ele={openDetailModal.data}
          />

          <ModelPreviewDefault
            isOpen={openScoreHistoryModal.isOpen}
            closeModel={() =>
              setOpenScoreHistoryModal({ isOpen: false, data: null })
            }
            previewHeading={"User Credit Score History"}
            modalClassName="!max-w-7xl "
            children={
              openScoreHistoryModal?.data ? (() => {
                const modalData = openScoreHistoryModal.data;
                const scoreInfo = getScoreLevelInfo(
                  modalData?.creditScore,
                  modalData?.creditScoreLevels
                );
                const historyList = Array.isArray(modalData?.history)
                  ? [...modalData.history].sort((a, b) => (b.actionAt || 0) - (a.actionAt || 0))
                  : [];
                const latestAction = historyList[0];

                return (
                  <div className=" p-6 bg-white shadow-lg rounded-lg space-y-6">
                    <div className="flex items-center space-x-4 justify-between">
                      <div className="flex items-center gap-2">
                        {modalData?.userId?.profile ? (
                          <img
                            className="rounded-full h-12 w-12 object-cover border border-gray-200 shadow-sm"
                            src={
                              modalData?.userId?.profile ||
                              "/img/person-304893_1280.png"
                            }
                            alt="User profile"
                            onError={(e) => {
                              e.currentTarget.src =
                                "/img/person-304893_1280.png";
                            }}
                          />
                        ) : (
                          <div className="bg-[#00811D] text-white font-bold text-xl rounded-full h-12 w-12 flex items-center justify-center">
                            {modalData?.userId?.name?.charAt(0)?.toUpperCase() || "U"}
                          </div>
                        )}

                        <div>
                          <h2 className="text-2xl font-semibold">
                            {modalData?.userId?.name || "N/A"}
                          </h2>
                          <p className="text-gray-500">
                            {modalData?.userId?.email || "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <p
                          className={`text-3xl font-bold transition-colors duration-300 ${getScoreColor(
                            modalData?.creditScore
                          )}`}
                        >
                          {modalData?.creditScore ?? "N/A"}
                        </p>
                        <p className="text-lg font-semibold">
                          Current Credit Score
                        </p>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-5 shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-blue-800 flex items-center gap-2">
                          Credit Overview
                          {latestAction && (
                            latestAction.actionType === "Down" ? (
                              <FaArrowTrendDown
                                size={20}
                                className="text-red-500"
                              />
                            ) : (
                              <FaArrowTrendUp
                                size={20}
                                className="text-green-500"
                              />
                            )
                          )}
                        </h3>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <span className="block text-sm font-semibold text-blue-700">
                            Status
                          </span>
                          <span className="text-gray-700">
                            {scoreInfo?.label || "N/A"}
                          </span>
                        </div>

                        <div>
                          <span className="block text-sm font-semibold text-blue-700">
                            Risk Level
                          </span>
                          <span className="text-gray-700">
                            {scoreInfo?.riskLevel || "N/A"}
                          </span>
                        </div>

                        <div>
                          <span className="block text-sm font-semibold text-blue-700">
                            Access
                          </span>
                          <span className="text-gray-700">
                            {scoreInfo?.accessLevel || "N/A"}
                          </span>
                        </div>

                        <div className="md:col-span-3">
                          <span className="block text-sm font-semibold text-blue-700">
                            Remarks
                          </span>
                          <p className="text-gray-700">
                            {stripHtml(scoreInfo?.remarks) || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        Credit Score History
                      </h3>
                      <div className="overflow-x-auto !max-h-[40vh] overflow-y-auto">
                        <table className="min-w-full table-auto border border-gray-200 rounded-md ">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="px-4 py-2 border">Event</th>
                              <th className="px-4 py-2 border">Type</th>
                              <th className="px-4 py-2 border">Change</th>
                              <th className="px-4 py-2 border">Previous</th>
                              <th className="px-4 py-2 border">New</th>
                              <th className="px-4 py-2 border">Remarks</th>
                              <th className="px-4 py-2 border">Timestamp</th>
                            </tr>
                          </thead>
                          <tbody>
                            {historyList.length > 0 ? (
                              historyList.map((item, index) => (
                                <tr
                                  key={index}
                                  className={
                                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                                  }
                                >
                                  <td className="px-4 py-2 border">
                                    {
                                      modalData?.creditScoreChangeEvents?.[item?.event]
                                        ?.label ||
                                      item?.event ||
                                      "N/A"
                                    }
                                  </td>
                                  <td
                                    className={`px-4 py-2 border font-semibold ${item?.actionType === "Up"
                                        ? "text-green-600"
                                        : "text-red-600"
                                      }`}
                                  >
                                    {item?.actionType || "N/A"}
                                  </td>
                                  <td className="px-4 py-2 border">
                                    {item?.changeValue ?? "N/A"}
                                  </td>
                                  <td className="px-4 py-2 border">
                                    {item?.previousScore ?? "N/A"}
                                  </td>
                                  <td className="px-4 py-2 border">
                                    {item?.newScore ?? "N/A"}
                                  </td>
                                  <td className="px-4 py-2 border">
                                    {stripHtml(item?.remarks) || "N/A"}
                                  </td>
                                  <td className="px-4 py-2 border">
                                    {item?.actionAt
                                      ? formatDateByFormat(item.actionAt)
                                      : "N/A"}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={7}>
                                  <div className="border border-gray-300 rounded-md">
                                    <NoData
                                      nodataLabel={"No records found."}
                                      nodataDesc={"There are no records to display."}
                                    />
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })() : null
            }
          />
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    loading: state?.monitoringUnits?.loading || false,
    ipAddressData: state?.monitoringUnits?.ipAddressList || {
      list: [],
      total: 0,
    },

    countryList: state?.monitoringUnits?.countryList || [],
    whiteBlackListData: state?.monitoringUnits?.whiteBlackList || {
      list: [],
      total: 0,
    },
    transactionListData: state?.monitoringUnits?.transactionList || {
      list: [],
      total: 0,
      link: "",
    },
    feesAnalysisListData: state?.monitoringUnits?.feesAnalysisList || {
      list: [],
      total: 0,
    },
    webStoresListData: state?.monitoringUnits?.webStoresList || {
      list: [],
      total: 0,
    },
    userCreditScoreMonitoringData: state?.monitoringUnits
      ?.userCreditScoreMonitoringList || {
      list: [],
      total: 0,
    },
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    ipAddressMonitoringList: (data) =>
      dispatch(monitoringUnitsAction.ipAddressMonitoringList(data)),
    getCountryCodeList: (data) =>
      dispatch(monitoringUnitsAction.getCountryCodeList(data)),
    ipAddressWhiteAndBlackListing: (data) =>
      dispatch(monitoringUnitsAction.ipAddressWhiteAndBlackListing(data)),
    showErrorMessage: (msg) => dispatch(alertActions.error(msg)),
    showSuccessMessage: (msg) => dispatch(alertActions.success(msg)),
    transactionMonitoring: (data) =>
      dispatch(monitoringUnitsAction.transactionMonitoring(data)),
    feesMonitoringList: (data) =>
      dispatch(monitoringUnitsAction.feesMonitoringList(data)),
    webStoresMonitoring: (data) =>
      dispatch(monitoringUnitsAction.webStoresMonitoring(data)),
    userCreditScoreMonitoring: (data) =>
      dispatch(monitoringUnitsAction.userCreditScoreMonitoring(data)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DetailsPage);
